import { useEffect, useState } from "react";
import { api, eur, openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, Loader2, FileDown } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

export default function Bilan() {
  const [budgets, setBudgets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newYear, setNewYear] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/budgets");
    setBudgets(data);
    if (data.length && !selected) setSelected(data[0]);
  };
  useEffect(() => { load(); }, []);

  const createBudget = async () => {
    if (!newYear) return;
    try {
      const defaultLines = [
        { category: "Cotisations", type: "entree", forecast: 0, actual: 0 },
        { category: "Dons", type: "entree", forecast: 0, actual: 0 },
        { category: "Subventions", type: "entree", forecast: 0, actual: 0 },
        { category: "Salaires", type: "sortie", forecast: 0, actual: 0 },
        { category: "Charges sociales", type: "sortie", forecast: 0, actual: 0 },
        { category: "Loyer", type: "sortie", forecast: 0, actual: 0 },
        { category: "Fournitures", type: "sortie", forecast: 0, actual: 0 },
        { category: "Événements", type: "sortie", forecast: 0, actual: 0 },
      ];
      const { data } = await api.post("/budgets", {
        year: Number(newYear), title: `Exercice ${newYear}`, lines: defaultLines, notes: "",
      });
      toast.success(`Bilan ${newYear} créé.`);
      setNewYear("");
      await load();
      setSelected(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const saveBudget = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload = {
        year: selected.year, title: selected.title, notes: selected.notes,
        lines: selected.lines.map((l) => ({
          ...l, forecast: Number(l.forecast) || 0, actual: Number(l.actual) || 0,
        })),
      };
      const { data } = await api.put(`/budgets/${selected.id}`, payload);
      toast.success("Bilan enregistré.");
      setSelected(data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const deleteBudget = async () => {
    if (!selected || !window.confirm(`Supprimer le bilan ${selected.year} ?`)) return;
    await api.delete(`/budgets/${selected.id}`);
    setSelected(null);
    load();
  };

  const addLine = () => {
    setSelected({
      ...selected,
      lines: [...(selected.lines || []), { category: "Nouvelle ligne", type: "sortie", forecast: 0, actual: 0 }],
    });
  };
  const removeLine = (i) => {
    setSelected({ ...selected, lines: selected.lines.filter((_, j) => j !== i) });
  };
  const updateLine = (i, patch) => {
    const lines = selected.lines.map((l, j) => (j === i ? { ...l, ...patch } : l));
    setSelected({ ...selected, lines });
  };

  const sum = (type, key) =>
    (selected?.lines || [])
      .filter((l) => l.type === type)
      .reduce((a, b) => a + (Number(b[key]) || 0), 0);

  const forecastSolde = sum("entree", "forecast") - sum("sortie", "forecast");
  const actualSolde = sum("entree", "actual") - sum("sortie", "actual");

  const chartData = (selected?.lines || []).map((l) => ({
    cat: l.category, Prévisionnel: Number(l.forecast) || 0, Définitif: Number(l.actual) || 0, type: l.type,
  }));

  return (
    <div data-testid="bilan-page">
      <PageHeader
        eyebrow="Exercice comptable"
        title="Bilan prévisionnel & définitif"
        description="Projetez les recettes/dépenses attendues et comparez-les aux montants réellement constatés."
        actions={
          <div className="flex items-center gap-2">
            <Input data-testid="new-year-input" placeholder="Année (ex: 2026)" value={newYear} onChange={(e) => setNewYear(e.target.value)} className="w-36" />
            <Button data-testid="new-budget-btn" onClick={createBudget} className="bg-brand-orange hover:bg-[#D04A06] text-white">
              <Plus size={16} className="mr-1" /> Créer un bilan
            </Button>
          </div>
        }
      />

      <div className="bg-white border border-brand-border rounded-xl p-4 mb-6 flex items-center gap-3 flex-wrap">
        <Label className="whitespace-nowrap">Exercice :</Label>
        <Select
          value={selected?.id || ""}
          onValueChange={(id) => setSelected(budgets.find((b) => b.id === id))}
        >
          <SelectTrigger className="w-60"><SelectValue placeholder="Choisir un bilan..." /></SelectTrigger>
          <SelectContent>
            {budgets.map((b) => <SelectItem key={b.id} value={b.id}>{b.year} — {b.title || "Exercice"}</SelectItem>)}
          </SelectContent>
        </Select>
        {selected && (
          <>
            <Button data-testid="export-budget-btn" variant="outline" onClick={() => openPdf(`/budgets/${selected.id}/pdf`)} className="ml-auto">
              <FileDown size={14} className="mr-1" /> PDF du bilan
            </Button>
            <Button data-testid="save-budget-btn" onClick={saveBudget} disabled={saving} className="bg-brand-navy text-white hover:bg-[#1D357D]">
              {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : <Save size={14} className="mr-1" />} Enregistrer
            </Button>
            <Button variant="outline" onClick={deleteBudget} className="text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 size={14} className="mr-1" /> Supprimer
            </Button>
          </>
        )}
      </div>

      {!selected ? (
        <div className="bg-white border border-brand-border rounded-xl p-10 text-center text-gray-400">
          Aucun bilan sélectionné. Créez un bilan pour commencer.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-brand-border rounded-xl p-5"><div className="small-caps">Entrées prévues</div><div className="font-serif text-2xl text-brand-green mt-1">{eur(sum("entree", "forecast"))}</div></div>
            <div className="bg-white border border-brand-border rounded-xl p-5"><div className="small-caps">Entrées réalisées</div><div className="font-serif text-2xl text-brand-green mt-1">{eur(sum("entree", "actual"))}</div></div>
            <div className="bg-white border border-brand-border rounded-xl p-5"><div className="small-caps">Sorties prévues</div><div className="font-serif text-2xl text-brand-orange mt-1">{eur(sum("sortie", "forecast"))}</div></div>
            <div className="bg-white border border-brand-border rounded-xl p-5"><div className="small-caps">Sorties réalisées</div><div className="font-serif text-2xl text-brand-orange mt-1">{eur(sum("sortie", "actual"))}</div></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white border border-brand-border rounded-xl p-5">
              <div className="small-caps">Solde prévisionnel</div>
              <div className="font-serif text-3xl text-brand-navy mt-1">{eur(forecastSolde)}</div>
            </div>
            <div className="bg-white border border-brand-border rounded-xl p-5">
              <div className="small-caps">Solde définitif</div>
              <div className={`font-serif text-3xl mt-1 ${actualSolde >= 0 ? "text-brand-green" : "text-brand-orange"}`}>{eur(actualSolde)}</div>
            </div>
          </div>

          <div className="bg-white border border-brand-border rounded-xl p-5 mb-6">
            <div className="small-caps">Comparaison</div>
            <h3 className="font-serif text-xl text-brand-navy mb-3">Prévisionnel vs définitif par catégorie</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid vertical={false} stroke="#F3F4F6" />
                  <XAxis dataKey="cat" tick={{ fontSize: 11, fill: "#6B7280" }} angle={-15} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} />
                  <Tooltip formatter={(v) => eur(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Prévisionnel" fill="#1E3A8A" radius={[6,6,0,0]} />
                  <Bar dataKey="Définitif" fill="#EA580C" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-brand-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-brand-border">
              <h3 className="font-serif text-xl text-brand-navy">Lignes budgétaires</h3>
              <Button size="sm" variant="outline" onClick={addLine}><Plus size={14} className="mr-1" /> Ajouter une ligne</Button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 small-caps">
                <tr>
                  <th className="text-left py-3 px-4">Catégorie</th>
                  <th className="text-left py-3 px-4">Type</th>
                  <th className="text-right py-3 px-4">Prévisionnel (€)</th>
                  <th className="text-right py-3 px-4">Définitif (€)</th>
                  <th className="text-right py-3 px-4">Écart</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {(selected.lines || []).map((l, i) => {
                  const diff = (Number(l.actual) || 0) - (Number(l.forecast) || 0);
                  return (
                    <tr key={i} className="border-t border-brand-border">
                      <td className="py-2 px-4">
                        <Input value={l.category} onChange={(e) => updateLine(i, { category: e.target.value })} className="border-0 shadow-none focus-visible:ring-0" />
                      </td>
                      <td className="py-2 px-4">
                        <Select value={l.type} onValueChange={(v) => updateLine(i, { type: v })}>
                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="entree">Entrée</SelectItem>
                            <SelectItem value="sortie">Sortie</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 px-4">
                        <Input type="number" step="0.01" value={l.forecast} onChange={(e) => updateLine(i, { forecast: e.target.value })} className="text-right" />
                      </td>
                      <td className="py-2 px-4">
                        <Input type="number" step="0.01" value={l.actual} onChange={(e) => updateLine(i, { actual: e.target.value })} className="text-right" />
                      </td>
                      <td className={`py-2 px-4 text-right font-medium ${diff >= 0 ? "text-brand-green" : "text-brand-orange"}`}>
                        {diff >= 0 ? "+" : ""}{eur(diff)}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => removeLine(i)}><Trash2 size={14} className="text-red-500" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="bg-white border border-brand-border rounded-xl p-5 mt-6">
            <Label>Notes et commentaires sur l'exercice</Label>
            <Textarea rows={3} value={selected.notes || ""} onChange={(e) => setSelected({ ...selected, notes: e.target.value })} />
          </div>
        </>
      )}
    </div>
  );
}
